import { Box3, BufferAttribute, BufferGeometry, DoubleSide, Ray, Vector3 } from 'three';
import { MeshBVH } from 'three-mesh-bvh';

/**
 * Bounds of the design region -- the design parts with every non-design part
 * taken away -- estimated from the parts' meshes, for previewing where the
 * runner places milling limits. The runner measures the same region exactly
 * with a Parasolid boolean on the STEP files; this estimate shoots a grid of
 * rays along each axis through both sets of meshes and keeps, on every ray,
 * the stretches inside a design part and outside every non-design part.
 * Along the axis a ray runs, a face it crosses is measured exactly; a feature
 * thinner than the grid spacing can fall between rays.
 */

/** Non-indexed triangles, 9 numbers per triangle, in the frame to measure in. */
export type TriangleSoup = Float32Array;

export type DesignRegionEstimate =
  | { kind: 'box'; box: Box3 }
  /** The non-design parts cover the design entirely. */
  | { kind: 'covered' }
  /** No ray crossed the meshes cleanly (open or broken meshes); the caller falls back. */
  | { kind: 'unknown' };

type Interval = [number, number];

/** Rays per side of the grid on each axis. */
const DEFAULT_SAMPLES = 48;

/**
 * Where a ray is inside a closed mesh, from the distances it crosses the
 * surface at: entry, exit, entry, exit... Hits closer than `eps` are one
 * crossing -- a ray through a shared edge or vertex hits every triangle
 * there. An odd count means the ray slipped through a gap, so null.
 */
export function crossingIntervals(distances: readonly number[], eps: number): Interval[] | null {
  const sorted = [...distances].sort((a, b) => a - b);
  const crossings: number[] = [];
  for (const d of sorted) {
    if (crossings.length === 0 || d - crossings[crossings.length - 1] > eps) crossings.push(d);
  }
  if (crossings.length % 2 !== 0) return null;
  const intervals: Interval[] = [];
  for (let i = 0; i < crossings.length; i += 2) intervals.push([crossings[i], crossings[i + 1]]);
  return intervals;
}

/** The intervals merged where they overlap or touch, sorted. */
export function unionIntervals(intervals: readonly Interval[]): Interval[] {
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const out: Interval[] = [];
  for (const [lo, hi] of sorted) {
    const last = out[out.length - 1];
    if (last && lo <= last[1]) {
      last[1] = Math.max(last[1], hi);
    } else {
      out.push([lo, hi]);
    }
  }
  return out;
}

/**
 * `a` with `b` taken away. Pieces no longer than `eps` are dropped: a
 * non-design part copied from a design region shares its faces, and float
 * noise on those faces would otherwise leave slivers.
 */
export function subtractIntervals(a: readonly Interval[], b: readonly Interval[], eps: number): Interval[] {
  const cut = unionIntervals(b);
  const out: Interval[] = [];
  for (const [lo, hi] of unionIntervals(a)) {
    let start = lo;
    for (const [cutLo, cutHi] of cut) {
      if (cutHi <= start || cutLo >= hi) continue;
      if (cutLo > start) out.push([start, cutLo]);
      start = Math.max(start, cutHi);
      if (start >= hi) break;
    }
    if (start < hi) out.push([start, hi]);
  }
  return out.filter(([lo, hi]) => hi - lo > eps);
}

function soupBox(soup: TriangleSoup): Box3 {
  const box = new Box3();
  const point = new Vector3();
  for (let i = 0; i + 2 < soup.length; i += 3) box.expandByPoint(point.set(soup[i], soup[i + 1], soup[i + 2]));
  return box;
}

function buildBvh(soup: TriangleSoup): MeshBVH {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(soup, 3));
  return new MeshBVH(geometry);
}

/**
 * Estimates the design region's bounds from the design and non-design
 * meshes. Each design part and each non-design part is one closed mesh;
 * parts of a kind may overlap one another.
 */
export function estimateDesignRegion(
  design: readonly TriangleSoup[],
  nondesign: readonly TriangleSoup[],
  options: { samples?: number } = {}
): DesignRegionEstimate {
  const designParts = design.filter((soup) => soup.length >= 9).map((soup) => ({ soup, box: soupBox(soup) }));
  if (designParts.length === 0) return { kind: 'unknown' };

  const designBox = new Box3();
  for (const part of designParts) designBox.union(part.box);
  // Only the non-design parts that reach the design can take anything away.
  const cutters = nondesign
    .filter((soup) => soup.length >= 9)
    .map((soup) => ({ soup, box: soupBox(soup) }))
    .filter((part) => part.box.intersectsBox(designBox));
  if (cutters.length === 0) return { kind: 'box', box: designBox };

  const size = designBox.getSize(new Vector3());
  const diagonal = size.length();
  const eps = Math.max(diagonal * 1e-5, 1e-9);
  const samples = Math.max(2, Math.floor(options.samples ?? DEFAULT_SAMPLES));

  const designBvhs = designParts.map((part) => ({ bvh: buildBvh(part.soup), box: part.box }));
  const cutterBvhs = cutters.map((part) => ({ bvh: buildBvh(part.soup), box: part.box }));

  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  let cleanRays = 0;
  const ray = new Ray();
  const lo = designBox.min.toArray();
  const hi = designBox.max.toArray();

  for (let axis = 0; axis < 3; axis++) {
    const u = (axis + 1) % 3;
    const v = (axis + 2) % 3;
    const start = lo[axis] - diagonal * 0.01 - 1;
    for (let i = 0; i < samples; i++) {
      for (let j = 0; j < samples; j++) {
        // Off the cell centres by an odd fraction, so a grid of rays does not
        // run exactly along the edges of a part drawn on round numbers.
        const origin = [0, 0, 0];
        origin[axis] = start;
        origin[u] = lo[u] + ((i + 0.5 + 0.0137) / samples) * (hi[u] - lo[u]);
        origin[v] = lo[v] + ((j + 0.5 - 0.0071) / samples) * (hi[v] - lo[v]);
        ray.origin.set(origin[0], origin[1], origin[2]);
        ray.direction.set(axis === 0 ? 1 : 0, axis === 1 ? 1 : 0, axis === 2 ? 1 : 0);

        const inside = (parts: { bvh: MeshBVH; box: Box3 }[]): Interval[] | null => {
          const intervals: Interval[] = [];
          for (const part of parts) {
            if (!ray.intersectsBox(part.box)) continue;
            const hits = part.bvh.raycast(ray, DoubleSide).map((hit) => hit.distance);
            const own = crossingIntervals(hits, eps);
            if (own === null) return null;
            intervals.push(...own);
          }
          return intervals;
        };

        const inDesign = inside(designBvhs);
        if (inDesign === null) continue;
        if (inDesign.length === 0) {
          cleanRays++;
          continue;
        }
        const inCutters = inside(cutterBvhs);
        if (inCutters === null) continue;
        cleanRays++;

        const left = subtractIntervals(inDesign, inCutters, eps);
        if (left.length === 0) continue;
        min[axis] = Math.min(min[axis], start + left[0][0]);
        max[axis] = Math.max(max[axis], start + left[left.length - 1][1]);
      }
    }
  }

  if (cleanRays === 0) return { kind: 'unknown' };
  if (![0, 1, 2].every((axis) => Number.isFinite(min[axis]) && Number.isFinite(max[axis]))) {
    // No ray on some axis found design left over: either it is all covered,
    // or what is left is thinner than the grid sees.
    return [0, 1, 2].some((axis) => Number.isFinite(min[axis])) ? { kind: 'unknown' } : { kind: 'covered' };
  }
  return { kind: 'box', box: new Box3(new Vector3(...min), new Vector3(...max)) };
}

/** Rays per side of the grid, on each axis, when measuring overlap volumes. */
const OVERLAP_SAMPLES = 96;

/** Share of unclean rays through a part above which its overlap is unknown. */
const MAX_UNCLEAN_SHARE = 0.05;

/** A repeatable uniform [0, 1) sequence (mulberry32), so a model always measures the same. */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Where `a` and `b` both are, without pieces no longer than `eps` (faces the two only share). */
export function intersectIntervals(a: readonly Interval[], b: readonly Interval[], eps: number): Interval[] {
  const left = unionIntervals(a);
  const right = unionIntervals(b);
  const out: Interval[] = [];
  for (const [aLo, aHi] of left) {
    for (const [bLo, bHi] of right) {
      const lo = Math.max(aLo, bLo);
      const hi = Math.min(aHi, bHi);
      if (hi - lo > eps) out.push([lo, hi]);
    }
  }
  return out;
}

function intervalsLength(intervals: readonly Interval[]): number {
  return intervals.reduce((total, [lo, hi]) => total + (hi - lo), 0);
}

type Cutter = { soup: TriangleSoup; box: Box3; bvh: () => MeshBVH };

/** One design part against the non-design parts that reach it, ray by ray. */
interface OverlapProbe {
  region: Box3;
  /** Where rays along `axis` start, before every mesh. */
  start(axis: number): number;
  /**
   * The stretches of the ray inside both the design part and some
   * non-design part, as distances from its origin; null when a mesh the ray
   * crosses is open there.
   */
  overlap(ray: Ray): Interval[] | null;
}

function cuttersOf(nondesign: readonly TriangleSoup[]): Cutter[] {
  return nondesign
    .filter((soup) => soup.length >= 9)
    .map((soup) => {
      let bvh: MeshBVH | null = null;
      return { soup, box: soupBox(soup), bvh: () => (bvh ??= buildBvh(soup)) };
    });
}

/** Null when the part cannot overlap any non-design part. */
function overlapProbe(soup: TriangleSoup, cutters: readonly Cutter[]): OverlapProbe | null {
  if (soup.length < 9) return null;
  const box = soupBox(soup);
  const reaching = cutters.filter((cutter) => cutter.box.intersectsBox(box));
  if (reaching.length === 0) return null;

  const cutBox = new Box3();
  for (const cutter of reaching) cutBox.union(cutter.box);
  const region = box.clone().intersect(cutBox);
  const size = region.getSize(new Vector3());
  if (region.isEmpty() || size.x <= 0 || size.y <= 0 || size.z <= 0) return null;

  const outer = box.clone().union(cutBox);
  const diagonal = outer.getSize(new Vector3()).length();
  const eps = Math.max(diagonal * 1e-5, 1e-9);
  const designBvh = buildBvh(soup);
  const distances = (bvh: MeshBVH, ray: Ray): number[] => bvh.raycast(ray, DoubleSide).map((hit) => hit.distance);

  return {
    region,
    start: (axis) => outer.min.getComponent(axis) - diagonal * 0.01 - 1,
    overlap: (ray) => {
      const inDesign = crossingIntervals(distances(designBvh, ray), eps);
      if (inDesign === null) return null;
      if (inDesign.length === 0) return [];
      const inCutters: Interval[] = [];
      for (const cutter of reaching) {
        if (!ray.intersectsBox(cutter.box)) continue;
        const own = crossingIntervals(distances(cutter.bvh(), ray), eps);
        if (own === null) return null;
        inCutters.push(...own);
      }
      return inCutters.length === 0 ? [] : intersectIntervals(inDesign, inCutters, eps);
    }
  };
}

/**
 * Casts a `samples`² grid of rays along `axis` across the probe's region,
 * one per cell at `offset()` within it, and hands each clean ray's overlap
 * to `visit`. Returns the share of rays that were clean.
 */
function castGrid(
  probe: OverlapProbe,
  axis: number,
  samples: number,
  offset: () => number,
  visit: (overlap: Interval[]) => void
): number {
  const u = (axis + 1) % 3;
  const v = (axis + 2) % 3;
  const lo = probe.region.min.toArray();
  const hi = probe.region.max.toArray();
  const start = probe.start(axis);
  const ray = new Ray();
  ray.direction.set(axis === 0 ? 1 : 0, axis === 1 ? 1 : 0, axis === 2 ? 1 : 0);
  let clean = 0;
  for (let i = 0; i < samples; i++) {
    for (let j = 0; j < samples; j++) {
      const origin = [0, 0, 0];
      origin[axis] = start;
      origin[u] = lo[u] + ((i + offset()) / samples) * (hi[u] - lo[u]);
      origin[v] = lo[v] + ((j + offset()) / samples) * (hi[v] - lo[v]);
      ray.origin.set(origin[0], origin[1], origin[2]);
      const overlap = probe.overlap(ray);
      if (overlap === null) continue;
      clean++;
      if (overlap.length > 0) visit(overlap);
    }
  }
  return clean / (samples * samples);
}

/**
 * The volume of each design part that lies inside non-design parts, in the
 * soups' units cubed; null for a part whose meshes are too broken to tell.
 * A grid of rays crosses the overlap region along each axis in turn; on every
 * ray the length inside the design part and inside any non-design part is
 * exact, so only the cross-section is sampled, at a random point per cell;
 * averaging the three axes evens out what a thin plate across the rays adds
 * or misses. Faces the parts merely share measure as no overlap.
 */
export function designOverlapVolumes(
  design: readonly TriangleSoup[],
  nondesign: readonly TriangleSoup[],
  options: { samples?: number } = {}
): (number | null)[] {
  const cutters = cuttersOf(nondesign);
  const samples = Math.max(2, Math.floor(options.samples ?? OVERLAP_SAMPLES));

  return design.map((soup) => {
    const probe = overlapProbe(soup, cutters);
    if (!probe) return 0;
    const size = probe.region.getSize(new Vector3()).toArray();
    const measured: number[] = [];
    for (let axis = 0; axis < 3; axis++) {
      const cellArea = (size[(axis + 1) % 3] / samples) * (size[(axis + 2) % 3] / samples);
      let length = 0;
      // A regular grid lines up with plates drawn on round numbers and over-
      // or under-counts them whole, so each cell takes a seeded random point.
      const cleanShare = castGrid(probe, axis, samples, seededRandom(axis + 1), (overlap) => {
        length += intervalsLength(overlap);
      });
      if (cleanShare === 0 || 1 - cleanShare > MAX_UNCLEAN_SHARE) continue;
      // Unclean rays are left out; the clean ones stand in for them.
      measured.push((length * cellArea) / cleanShare);
    }
    return measured.length === 0 ? null : measured.reduce((total, value) => total + value, 0) / measured.length;
  });
}
